import React, { PureComponent, createRef } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { AssetProps } from './Helpers';
import { getIsTouchscreen } from '../../common/UiHelpers';
import { getIsMobile } from '../../store/selectors/ui.selectors';

import ErrorBoundary from './ErrorBoundary';
import Asset from './Asset';
import ProductCardContainer from '../shopping/ProductCard';

export const CONSIDER_VISITED_TIMEOUT = 300;

export class ShoppingSpace extends PureComponent {
  state = {
    clickedPin: null,
    hoveredPin: null,
  };

  itemViewedCallbacks = {};

  errorMessage = 'Something went wrong rendering the shopping space :(';

  constructor(props) {
    super(props);

    this.pinsContainerRef = createRef();

    this.getPinsContainerRef = this.getPinsContainerRef.bind(this);
    this.getItemPosition = this.getItemPosition.bind(this);
    this.renderPin = this.renderPin.bind(this);
    this.renderPins = this.renderPins.bind(this);
    this.toggleClicked = this.toggleClicked.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isCurrent && !this.props.isCurrent) {
      this.setState({ clickedPin: null, hoveredPin: null });
    }
  }

  renderCover() {
    const {
      productCollection: {
        assets: [cover] = [],
      } = {},
    } = this.props;

    return cover ? (
      <div className="shopping-space__cover">
        <Asset asset={cover} />
      </div>
    ) : null;
  }

  getPinsContainerRef() {
    return this.pinsContainerRef;
  }

  getItemPosition({ coordinateX, coordinateY }) {
    const {
      productCollection: {
        assets: [cover],
      },
      isMobile,
    } = this.props;
    const aspect = isMobile ? 0.8 : 2;

    // calculate cropped image size
    const horizontalCrop = Math.min(cover.width, cover.height * aspect);
    const verticalCrop = Math.min(cover.width / aspect, cover.height);

    // calculate coordinates on the cropped image
    const croppedCoordinateX = coordinateX - ((cover.width - horizontalCrop) / 2);
    const croppedCoordinateY = coordinateY - ((cover.height - verticalCrop) / 2);

    // transform coordinates to percentages for CSS
    // these are places pins will point to
    const left = `${(croppedCoordinateX / horizontalCrop) * 100}%`;
    const top = `${(croppedCoordinateY / verticalCrop) * 100}%`;

    // calculate if the content should be moved left, or right
    if (!this.getPinsContainerRef().current) {
      return {
        top,
        left,
      };
    }
    const containerWidth = this.getPinsContainerRef().current.offsetWidth;
    // calcualte x coordinate in pixels
    const leftPx = (croppedCoordinateX / horizontalCrop) * containerWidth;
    const pinContentWidth = isMobile ? 8 * 16 : 10 * 16;
    const canvasSpacing = 20;
    const pointerWidth = 16;
    // define max shift to prevent __pin-content and __pin-anchor (pointer) visual separation
    const maxHorizontalShift = (pinContentWidth - pointerWidth) / 2;
    let horizontalShift = 0;
    let layout = null;
    // check if the pin fits container and it padding. zero overflow means that it does
    const leftOverflow = Math.min(0, leftPx - canvasSpacing - (pinContentWidth / 2));
    const rightOverflow = Math.max(0, leftPx + canvasSpacing + (pinContentWidth / 2) - this.getPinsContainerRef().current.offsetWidth);
    if (leftOverflow < 0) {
      horizontalShift = Math.max(leftOverflow, maxHorizontalShift * -1);
      layout = 'right';
    } else if (rightOverflow > 0) {
      horizontalShift = Math.min(rightOverflow, maxHorizontalShift);
      layout = 'left';
    }
    return {
      top,
      left,
      horizontalShift,
      layout,
    };
  }

  toggleClicked(index) {
    const { clickedPin } = this.state;
    this.setState({ clickedPin: index !== clickedPin ? index : null });
  }

  setHovered(index) {
    const { clickedPin } = this.state;
    this.setState({
      hoveredPin: index,
      clickedPin: index === clickedPin ? clickedPin : null,
    });
  }

  resetHovered() {
    this.setState({
      hoveredPin: null,
    });
  }

  renderPin(item, index) {
    const { isCurrent, shouldRenderAffirm } = this.props;

    const { clickedPin, hoveredPin } = this.state;
    const {
      productCollection: {
        assets: [cover],
      },
    } = this.props;
    if (!cover) return null;
    const { top, left, layout } = this.getItemPosition(item);

    const handleClick = (e) => {
      e.stopPropagation();
      if (getIsTouchscreen()) {
        this.toggleClicked(index);
        this.itemViewedCallbacks[index]();
      }
    };

    const handleHover = (e) => {
      e.stopPropagation();
      this.setHovered(index);
      this.itemViewedCallbackTimeoutID = setTimeout(() => {
        this.itemViewedCallbacks[index]();
      }, CONSIDER_VISITED_TIMEOUT);
    };

    const handleHoverEnd = (e) => {
      e.stopPropagation();
      this.resetHovered();
      clearTimeout(this.itemViewedCallbackTimeoutID);
    };

    const setItemViewedCallback = (itemViewedCallback) => {
      this.itemViewedCallbacks[index] = itemViewedCallback;
    };

    return (
      <div
        key={index}
        className={classNames('shopping-space__pin-anchor', {
          'shopping-space__pin-anchor--visible': isCurrent,
          'shopping-space__pin-anchor--open': index === clickedPin || index === hoveredPin,
        })}
        style={{ top, left }}
        onClick={handleClick}
        onMouseEnter={handleHover}
        onMouseLeave={handleHoverEnd}
      >
        <div className={classNames('shopping-space__pin-content-anchor', { [`shopping-space__pin-content-anchor--${layout}`]: layout })}>
          <div className={classNames('shopping-space__pin-content', { [`shopping-space__pin-content--${layout}`]: layout })}>
            <ProductCardContainer
              item={item.item}
              sku={item.sku}
              addOnItems={item.addOnItems}
              hideImage={true}
              hideColorPicker={true}
              hideAffirm={!shouldRenderAffirm}
              underline={true}
              trackingName="Product Pin"
              setItemViewedCallback={setItemViewedCallback}
              source="shopping-space"
            />
          </div>
        </div>
        <div className={classNames('shopping-space__pin-triangle', { [`shopping-space__pin-triangle--${layout}`]: layout })} />
        <div className="shopping-space__pin-circle" />
      </div>
    );
  }

  renderPins() {
    const {
      productCollection: {
        productCollectionItems = [],
      } = {},
    } = this.props;

    return (
      <div
        className="shopping-space__pins-container"
        ref={this.pinsContainerRef}
      >
        {productCollectionItems
          .filter(({ type }) => type === 'ProductCard')
          .map(this.renderPin)}
      </div>
    );
  }

  render() {
    return (
      <ErrorBoundary errorMessage={this.errorMessage}>
        <div
          className="shopping-space"
          onClick={() => { this.setState({ clickedPin: null }); }}
        >
          {this.renderCover()}
          {this.renderPins()}
        </div>
      </ErrorBoundary>
    );
  }
}

ShoppingSpace.defaultProps = {
  isCurrent: false,
  productCollection: {},
};

const productCollectionItemShape = PropTypes.shape({
  type: PropTypes.oneOf(['ProductCard']),
  sku: PropTypes.string,
});

export const productCollectionShape = PropTypes.shape({
  images: PropTypes.arrayOf(AssetProps),
  productCollectionItems: PropTypes.arrayOf(productCollectionItemShape),
});

ShoppingSpace.propTypes = {
  isCurrent: PropTypes.bool,
  shouldRenderAffirm: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  productCollection: productCollectionShape,
};

const mapStateToProps = (state) => ({
  isMobile: getIsMobile(state),
});

export default connect(mapStateToProps, null)(ShoppingSpace);
